import {LegendItem} from 'chart.js';

import {
  BarChartCategoryData,
  CategoricalStatResponse,
  FeatureStatsApiResponse,
} from 'app/components/Dashboard/types';
import {ApiOrganizationAreaUnit} from 'app/modules/Remote/Organization';
import * as chartUtils from 'app/src/components/Dashboard/utils/chartUtils';
import {
  formattedFeatureName,
  generateChartColors,
} from 'app/src/components/Dashboard/utils/chartUtils';
import * as C from 'app/utils/constants';
import * as conversionUtils from 'app/utils/conversionUtils';
import * as layerUtils from 'app/utils/layerUtils';

/**
 * Returns the categorical data for the given data, layer key, layer legend map, time aggregation, and area unit.
 *
 * The function takes the API response that contains the stats for each category indexed by feature id
 * and returns different datasets for Chart.js:
 *
 * 1) `categoryChartData`: Aggregated data for all features by category.
 * 2) `singleCategoryChartDatas`: Data per-category per-feature.
 * 3) `categoryChartDataPercentages`: `categoryChartData` expressed as percentages of the total at each timestep.
 * 4) `singleCategoryChartDatasPercentages`: `singleCategoryChartDatas` expressed as percentages of the total at each timestep.
 *
 * The logic processes the input in one pass, caching date adjustments and step strings,
 * and avoids moment.js for performance.
 *
 * @returns An object containing raw and percentage series for Chart.js.
 */

export function getCategoricalData(
  data: CategoricalStatResponse,
  layerKey: string,
  layerLegendMap: Record<string, {color: string; label: string}>,
  timeAggregation: 'monthly' | 'yearly',
  areaUnit: ApiOrganizationAreaUnit = C.UNIT_AREA_ACRE,
  allDates: string[],
  includedCategories?: string[]
): {
  categoryChartData: BarChartCategoryData[];
  singleCategoryChartDatas: Record<string, BarChartCategoryData[]>;
  categoryChartDataPercentages: BarChartCategoryData[];
  singleCategoryChartDatasPercentages: Record<string, BarChartCategoryData[]>;
} {
  const conversionFactor = conversionUtils.getConversion(C.UNIT_AREA_M2, areaUnit);

  /* Build helper maps and capture timestamps to organize into more efficient data structures
   *
   * categoryMap will help us with category totals:
   * Output will look like:
   *
   * {
   *   '0': Map(7), // 0 is the category key
   *   '1': Map(7) {
   *     '12-2017' => 92.67058183098986,
   *     '12-2018' => 103.95481701991808,
   *     '12-2019' => 101.85543221434718,
   *     '12-2020' => 103.07419986748566,
   *     '12-2021' => 97.2929908
   *   },
   *   ...
   * }
   *
   *
   * featureMap will help us with feature breakdowns, this is a more efficient structure
   * of the api response. Output will be a nested Map structure:
   *
   * featureMap: Map(573) {
   *   '115388176' => Map(7), // 115388176 is the feature id
   *   '115388177' => Map(7),
   *   ...
   *
   *   // Each feature ID maps to another Map where:
   *   '115388277' => Map(7) {
   *     // Keys are category IDs ("1", "2", "4", etc)
   *     "1" => Map(7),
   *     "4" => Map(7) {
   *       // Which map years to values like above
   *       '2017' => 0,
   *       '2018' => 0,
   *     },
   *     "5" => Map(7),
   *     ...
   *   }
   * }
   *
   */
  const {categoryMap, featureMap, categories} = buildStatsMaps(
    data,
    layerKey,
    timeAggregation,
    conversionFactor
  );

  // Optionally restrict which categories get their own series (e.g. only Moderate/High/Severe).
  // We keep the *full* category set for the step totals below so percentages stay relative to the
  // feature's whole area rather than being re-normalized to just the visible subset.
  const includeSet =
    includedCategories && includedCategories.length ? new Set(includedCategories) : null;
  const displayedCategories = includeSet
    ? new Set([...categories].filter((category) => includeSet.has(category)))
    : categories;

  // Generate timeline steps and total area per step using the provided allDates
  const {allSteps, totalByStep} = buildStepsFromProvidedDates(
    allDates,
    timeAggregation,
    categories,
    categoryMap,
    layerKey
  );

  // Assemble multi-category series (area & percentage)
  const {area: categoryChartData, percentage: categoryChartDataPercentages} = makeCategorySeries(
    allSteps,
    displayedCategories,
    categoryMap,
    totalByStep,
    layerLegendMap
  );

  // Assemble per-feature series (area & percentage)
  const {area: singleCategoryChartDatas, percentage: singleCategoryChartDatasPercentages} =
    makeFeatureSeries(allSteps, displayedCategories, featureMap, totalByStep, data);

  return {
    categoryChartData,
    singleCategoryChartDatas,
    categoryChartDataPercentages,
    singleCategoryChartDatasPercentages,
  };
}

/**
 * Aggregates raw stats into two helper maps.
 * Also collects the set of categories and min/max timestamps.
 */
function buildStatsMaps(
  data: CategoricalStatResponse,
  layerKey: string,
  timeAggregation: 'monthly' | 'yearly',
  conversionFactor: number
): {
  categoryMap: Map<string, Map<string, number>>;
  featureMap: Map<string, Map<string, Map<string, number>>>;
  categories: Set<string>;
} {
  // Cache adjusted dates and step strings to avoid recalculating them
  const adjustedDateCache = new Map<string, Date>();

  // Cache timestepstrings to avoid recalculating them
  const stepCache = new Map<number, string>();

  // Map of category IDs to timestep to area
  const categoryMap = new Map<string, Map<string, number>>();

  // Map of feature IDs to category IDs to timestep to area
  const featureMap = new Map<string, Map<string, Map<string, number>>>();

  // Set of unique category IDs so we only show the categories that exist in the data
  const categories = new Set<string>();

  for (const [featureId, featureStats] of Object.entries(data)) {
    if ('error' in featureStats) continue;
    for (const [rawDate, categoryAreas] of Object.entries(featureStats.stats)) {
      // A date can map to a null/undefined stats object (e.g. a scene with no
      // data), which would throw in `Object.entries` below. Skip it.
      if (!categoryAreas) continue;

      // Adjust date once as `conditionallyAdjustLayerDate` is expensive due to moment.js
      let dateObj = adjustedDateCache.get(rawDate);
      if (!dateObj) {
        dateObj = layerUtils.conditionallyAdjustLayerDate(rawDate, layerKey).toDate();
        adjustedDateCache.set(rawDate, dateObj);
      }
      const timestep = dateObj.getTime();

      // Compute step string once
      let step = stepCache.get(timestep);
      if (!step) {
        step =
          timeAggregation === 'monthly'
            ? `${String(dateObj.getUTCMonth() + 1).padStart(2, '0')}-${dateObj.getUTCFullYear()}`
            : String(dateObj.getUTCFullYear());
        stepCache.set(timestep, step);
      }

      // Bucket areas
      for (const [category, areaM2] of Object.entries(categoryAreas)) {
        categories.add(category);
        const y = areaM2 ? areaM2 * conversionFactor : 0;

        // Multi-category
        const categoryTimes = categoryMap.get(category) ?? new Map();
        categoryTimes.set(step, (categoryTimes.get(step) ?? 0) + y);
        categoryMap.set(category, categoryTimes);

        // Single-feature
        const featureCategories = featureMap.get(featureId) ?? new Map();
        const featureTimes = featureCategories.get(category) ?? new Map();
        featureTimes.set(step, (featureTimes.get(step) ?? 0) + y);
        featureCategories.set(category, featureTimes);
        featureMap.set(featureId, featureCategories);
      }
    }
  }

  return {categoryMap, featureMap, categories};
}

/**
 * Creates the ordered array of timestep labels and a Map of total area per step.
 */
function buildStepsFromProvidedDates(
  allDates: string[],
  timeAggregation: 'monthly' | 'yearly',
  categories: Set<string>,
  categoryMap: Map<string, Map<string, number>>,
  layerKey: string
): {allSteps: string[]; totalByStep: Map<string, number>} {
  // Format dates according to timeAggregation
  const allSteps = allDates.map((date) =>
    chartUtils.formatAggregatedDate(date, timeAggregation, layerKey)
  );

  // Calculate total area for each step
  const totalByStep = new Map<string, number>();
  for (const step of allSteps) {
    let sum = 0;
    for (const cat of categories) {
      sum += categoryMap.get(cat)?.get(step) ?? 0;
    }
    totalByStep.set(step, sum);
  }

  return {allSteps, totalByStep};
}

/**
 * Builds Chart.js-compatible series for all categories,
 * returning both area and percentage datasets.
 */
function makeCategorySeries(
  allSteps: string[],
  categories: Set<string>,
  categoryMap: Map<string, Map<string, number>>,
  totalByStep: Map<string, number>,
  legendMap: Record<string, {color: string; label: string}>
): {
  area: BarChartCategoryData[];
  percentage: BarChartCategoryData[];
} {
  const areaData: BarChartCategoryData[] = [];
  const percentData: BarChartCategoryData[] = [];
  for (const category of categories) {
    const times = categoryMap.get(category)!;
    areaData.push({
      id: category,
      label: legendMap[category]?.label,
      backgroundColor: legendMap[category]?.color,
      data: allSteps.map((step) => times.get(step) ?? 0),
      category: category,
      type: 'bar',
    });
    percentData.push({
      id: category,
      label: legendMap[category]?.label,
      backgroundColor: legendMap[category]?.color,
      data: allSteps.map((step) => {
        const total = totalByStep.get(step) ?? 0;
        return total > 0 ? ((times.get(step) ?? 0) / total) * 100 : 0;
      }),
      category: category,
      type: 'bar',
    });
  }
  return {area: areaData, percentage: percentData};
}

/**
 * Builds Chart.js-compatible series for each feature within each category,
 * returning both area and percentage datasets.
 */
function makeFeatureSeries(
  allSteps: string[],
  categories: Set<string>,
  featureMap: Map<string, Map<string, Map<string, number>>>,
  totalByStep: Map<string, number>,
  data: CategoricalStatResponse
): {
  area: Record<string, BarChartCategoryData[]>;
  percentage: Record<string, BarChartCategoryData[]>;
} {
  const areaData: Record<string, BarChartCategoryData[]> = {};
  const percentData: Record<string, BarChartCategoryData[]> = {};
  const colors = generateChartColors(featureMap.size);

  for (const category of categories) {
    areaData[category] = [];
    percentData[category] = [];
    let i = 0;
    for (const [featId, featCats] of featureMap) {
      const times = featCats.get(category) ?? new Map();
      const label = !('error' in data[featId])
        ? formattedFeatureName(data[featId].properties)
        : featId;
      const properties = !('error' in data[featId]) ? data[featId].properties : undefined;
      const color = colors[i++];

      areaData[category].push({
        id: featId,
        properties: properties,
        label,
        backgroundColor: color,
        data: allSteps.map((s) => times.get(s) ?? 0),
        type: 'bar',
      });
      percentData[category].push({
        id: featId,
        properties: properties,
        label,
        backgroundColor: color,
        data: allSteps.map((s) => {
          const total = totalByStep.get(s) ?? 0;
          return total > 0 ? ((times.get(s) ?? 0) / total) * 100 : 0;
        }),
        type: 'bar',
      });
    }
  }
  return {area: areaData, percentage: percentData};
}

export const getCategoricalLegendItems = ({
  data,
  displayFeatureIds,
  layerKey,
  statType,
  includedCategories,
}: {
  data: FeatureStatsApiResponse;
  displayFeatureIds: Set<string>;
  layerKey: string;
  statType: string;
  includedCategories?: string[];
}): LegendItem[] => {
  const dataColors = generateChartColors(Object.keys(data).length);
  const layer = layerUtils.getLayer(layerKey);

  // If we selecting specific features in the category legends, we just using
  // the chartjs legend instead of returning our own special ones.
  if (displayFeatureIds.size > 0) return [];

  // for area counts, we iterating through each possible category in the layer,
  // filtering out any categories not present in the data, and returning the
  // labels and colors from the layerLegendMap.

  if (statType === 'area_counts') {
    if (!layer || layer.type !== 'image' || !layer.layerLegendMap) return [];

    const includeSet =
      includedCategories && includedCategories.length ? new Set(includedCategories) : null;

    const categoryIds = new Set(
      Object.values(data).flatMap((featureData) => {
        if ('error' in featureData) return [];
        return Object.keys(featureData.stats[Object.keys(featureData.stats)[0]] || {});
      })
    );

    return Array.from(categoryIds)
      .filter((categoryId) => !includeSet || includeSet.has(categoryId))
      .map((categoryId, index) => {
      const legendInfo = layer.layerLegendMap![categoryId];
      return {
        label: categoryId,
        text: legendInfo?.label || categoryId,
        datasetIndex: index,
        fillStyle: legendInfo?.color,
      };
    });
  }

  // for Area Percentages, we're checking the percentage of a particular
  // category broken down by feature. so we iterating though each feature
  // and returning the feature name and the selected color for its index.

  if (statType === 'area_percentages') {
    return Object.entries(data).map(([id, featureData], index) => ({
      label: id,
      text: featureData.properties?.name,
      datasetIndex: index,
      fillStyle: dataColors[index],
    }));
  }

  return [];
};
